/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Box, Typography, styled, useTheme } from '@mui/material';
import { BackButton } from '../BackButton';
import TestIds from '../../testIds';
import { clickHandler } from '../../util/clickHandler';

const Stepper = styled('div')`
  border-radius: 8px;
  flex-grow: 1;
  height: 6px;
  background-color: ${({ color }) => color};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : '')};
  position: relative;

  ::before {
    content: ' ';
    position: absolute;
    top: -10px;
    bottom: -10px;
    right: -10px;
    left: -10px;
  }
`;

export type StepperProps = Readonly<{
  activeStep: number;
  maxSteps: number;
  previousStep?: number;
  title: string;
  setPageIndex: (value: number) => void;
  enableBackBtn?: boolean;
  disableStepperClick?: boolean;
}>;

export const Steppers: React.FC<StepperProps> = (props) => {
  const theme = useTheme();

  return (
    <Box gap={'8px'} display={'flex'} flexDirection={'column'} data-testid={'steppers-container'}>
      {/* Stepper box */}
      {/* default is show back button */}
      {props.enableBackBtn || props.enableBackBtn === undefined ? (
        <Box height={'30px'}>
          {props.activeStep > 0 && (
            <BackButton
              data-testid={TestIds.components.stepper.back}
              text='Back'
              onClick={() => {
                props.setPageIndex((props.activeStep - 1) % props.maxSteps);
              }}
            />
          )}
        </Box>
      ) : null}
      <Typography variant='h4'>{props.title}</Typography>
      <Box gap={'16px'} flexDirection={'row'} display='flex'>
        {Array.from({ length: props.maxSteps }, (_, i) => (
          <Stepper
            key={i}
            role='tab'
            tabIndex={0}
            aria-label={`Step ${i + 1}`}
            aria-selected={props.activeStep === i ? 'true' : 'false'}
            {...(props.disableStepperClick ? {} : clickHandler(() => props.setPageIndex(i)))}
            color={props.activeStep >= i ? theme.colors.primary700 : theme.colors.primary200}
          />
        ))}
      </Box>
      <Typography
        variant='body2'
        color={theme.colors.grey700}
        data-testid={TestIds.components.stepper.step}
        id={TestIds.components.stepper.step}
        data-previous-step={props.previousStep ? props.previousStep + 1 : props.previousStep}
        data-current-step={props.activeStep + 1}
        data-max-step={props.maxSteps}
      >
        Step {props.activeStep + 1} of {props.maxSteps}
      </Typography>
    </Box>
  );
};

export default Steppers;
